import React, { Dispatch, SetStateAction } from 'react'
import { useParams } from 'react-router-dom'
import * as Api from 'src/api'
import { UserIcon } from 'src/assets/icons/customIcons/menu-icons/User'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import * as Table from 'src/tailwind/components/Table'
import Loader from 'src/views/components/Loader'

interface PopupProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  semester?: number
  userName: string
  userDegree?: Api.StudentDegree
  lecturerProfileId: string
}

export default function MessageInfoPopup({
  open,
  setOpen,
  semester,
  userName,
  userDegree,
  lecturerProfileId,
}: PopupProps) {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const { messageId, semesterId } = useParams()

  // the endpoint answers only for a lecturer profile, which may not be the active one
  const lecturerHeaders = React.useMemo(
    () => ({
      get 'Accept-Language'() {
        return headers['Accept-Language']
      },
      get Authorization() {
        return headers.Authorization
      },
      'Profile-ID': lecturerProfileId,
    }),
    [headers, lecturerProfileId]
  )

  const studentInfo = useApi({
    endpoint: Api.getStudentByMessage,
    params: React.useMemo(
      () => ({
        headers: lecturerHeaders,
        query: {
          semId: semesterId!,
        },
        args: {
          messageId: messageId!,
        },
      }),
      [lecturerHeaders, messageId, semesterId]
    ),
    shouldFetch: open,
    suspense: false,
  })

  const handleClose = React.useCallback(() => setOpen(false), [setOpen])

  const groups = studentInfo.data?.groups ?? []
  const degree = groups[0]?.course?.degree ?? userDegree
  const program = studentInfo.data?.profilePrograms?.customProgram

  return (
    <Modal
      title={t('resumeLayout:personal_information')}
      className="notify-modal"
      isModalOpen={open}
      onClose={handleClose}
      icon={<UserIcon />}
      footer={
        <div className="flex justify-end">
          <Button variant="blue" type="button" onClick={handleClose}>
            {t('common:close')}
          </Button>
        </div>
      }
    >
      <h2>{t('course:student_profile')}</h2>
      <div className="ml-2 flex">
        <div className="mr-5 text-end font-bold">
          <p>{t('uncategorized:name_surname')}</p>
          <p>{t('person:degree')}</p>
          <p>{t('faculty:faculty_name')}</p>
          <p>{t('program:programme')}</p>
          <p>{t('common:academic_semester')}</p>
        </div>
        <div className="align-start">
          <p>{userName}</p>
          <p className="min-h-[20px]">{degree != null ? t(`degrees:${degree}`) : ''}</p>
          <p className="min-h-[20px]">{studentInfo.data?.profilePrograms?.faculty.name}</p>
          <p className="min-h-[20px]">{program != null ? `${program.code} - ${program.name}` : ''}</p>
          <p>{semester ?? ''}</p>
        </div>
      </div>
      <h2 className="mb-2 mt-5">{t('notification:your_subjects_chosen_by_given_student')}</h2>
      <Table.Table className="w-full">
        <Table.Thead>
          <Table.Tr className="border-t-0">
            <Table.Th className="w-[55%] !py-[10px]">{t('common:name')}</Table.Th>
            <Table.Th className="w-[15%] !py-[10px]">{t('common:code')}</Table.Th>
            <Table.Th className="w-[15%] !py-[10px]">{t('group:group')}</Table.Th>
            <Table.Th className="w-[15%] !py-[10px]">{t('course:credit')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {studentInfo.isLoading ? (
            <Table.Tr className="border-none">
              <Table.Td colSpan={4} className="border-none">
                <Loader className="m-auto flex" />
              </Table.Td>
            </Table.Tr>
          ) : groups.length === 0 ? (
            <Table.Tr className="border-none">
              <Table.Td colSpan={4} className="border-none text-center">
                {t('error:records_not_found')}
              </Table.Td>
            </Table.Tr>
          ) : (
            groups.map((group) => (
              <Table.Tr key={group.id}>
                <Table.Td className="!py-[10px]">{group.course?.name}</Table.Td>
                <Table.Td className="!py-[10px]">{group.course?.code}</Table.Td>
                <Table.Td className="!py-[10px]">{group.name}</Table.Td>
                <Table.Td className="!py-[10px]">{group.course?.credits.toFixed(2)}</Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table.Table>
    </Modal>
  )
}
